import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../Utils/AxiosInstance";

const initialState = {
  kpiData: null,
  salesLeaderboardData: null,
  companySalesFunnelData: null,
  teamActivePipelineData: null,
  hotLeadsData: null,
  leadsByCategoryData: null,
  isSuccess: false,
  message: null,
  status: "idle", //'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const fetchAdminKpis = createAsyncThunk(
  "adminDashboard/fetchKpis",
  async ({ startDate, endDate, pipelineId } = {}, { rejectWithValue }) => {
    try {
      let url = "/Dashbord/kpis";
      if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }
      if (pipelineId) {
        url += (url.includes("?") ? "&" : "?") + `pipelineId=${pipelineId}`;
      }
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchSalesLeaderboard = createAsyncThunk(
  "adminDashboard/fetchSalesLeaderboard",
  async ({ limit = 10, startDate, endDate, pipelineId } = {}, { rejectWithValue }) => {
    try {
      let url = `/Dashbord/salesLeaderboard?limit=${limit}`;
      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      if (pipelineId) {
        url += `&pipelineId=${pipelineId}`;
      }
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchCompanySalesFunnel = createAsyncThunk(
  "adminDashboard/fetchCompanySalesFunnel",
  async ({ startDate, endDate, pipelineId } = {}, { rejectWithValue }) => {
    try {
      let url = `/Dashbord/companySalesFunnel`;
      if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }
      if (pipelineId) {
        url += (url.includes("?") ? "&" : "?") + `pipelineId=${pipelineId}`;
      }
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchLeadsBySource = createAsyncThunk(
  "adminDashboard/fetchLeadsBySource",
  async ({ startDate, endDate, pipelineId } = {}, { rejectWithValue }) => {
    try {
      let url = `/Dashbord/leadsBySourceDonut`;
      if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }
      if (pipelineId) {
        url += (url.includes("?") ? "&" : "?") + `pipelineId=${pipelineId}`;
      }
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchTeamActivePipeline = createAsyncThunk(
  "adminDashboard/fetchTeamActivePipeline",
  async (pipelineId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/Dashbord/teamActivePipeline?pipelineId=${pipelineId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchHotLeads = createAsyncThunk(
  "adminDashboard/fetchHotLeads",
  async ({ limit = 10, pipelineId } = {}, { rejectWithValue }) => {
    try {
      let url = `/Dashbord/hotLeads?limit=${limit}`;
      if (pipelineId) {
        url += `&pipelineId=${pipelineId}`;
      }
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchLeadsByCategory = createAsyncThunk(
  "adminDashboard/fetchLeadsByCategory",
  async ({ projectId, startDate, endDate, pipelineId } = {}, { rejectWithValue }) => {
    try {
      let url = `/Dashbord/leadsByCategory?`;
      const params = new URLSearchParams();
      if (projectId) params.append("projectId", projectId);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (pipelineId) params.append("pipelineId", pipelineId);
      url += params.toString();

      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const AdminDashboardSlice = createSlice({
  name: "AdminDashboard",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchAdminKpis.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAdminKpis.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isSuccess = action.payload?.isSuccess || true;
        state.message = action.payload?.message || "Success";
        state.kpiData = action.payload?.data || null;
      })
      .addCase(fetchAdminKpis.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchSalesLeaderboard.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSalesLeaderboard.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.salesLeaderboardData = action.payload?.data || [];
      })
      .addCase(fetchSalesLeaderboard.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchCompanySalesFunnel.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCompanySalesFunnel.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.companySalesFunnelData = action.payload?.data || null;
      })
      .addCase(fetchCompanySalesFunnel.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchLeadsBySource.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchLeadsBySource.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.leadsBySourceData = action.payload?.data || null;
      })
      .addCase(fetchLeadsBySource.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchTeamActivePipeline.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchTeamActivePipeline.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.teamActivePipelineData = action.payload?.data || null;
      })
      .addCase(fetchTeamActivePipeline.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchHotLeads.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchHotLeads.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.hotLeadsData = action.payload?.data || [];
      })
      .addCase(fetchLeadsByCategory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchLeadsByCategory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.leadsByCategoryData = action.payload?.data || [];
      })
      .addCase(fetchLeadsByCategory.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

export const getAdminKpiData = (state) => state.AdminDashboard.kpiData;
export const getSalesLeaderboardData = (state) => state.AdminDashboard.salesLeaderboardData;
export const getCompanySalesFunnelData = (state) => state.AdminDashboard.companySalesFunnelData;
export const getLeadsBySourceData = (state) => state.AdminDashboard.leadsBySourceData;
export const getTeamActivePipelineData = (state) => state.AdminDashboard.teamActivePipelineData;
export const getHotLeadsData = (state) => state.AdminDashboard.hotLeadsData;
export const getLeadsByCategoryData = (state) => state.AdminDashboard.leadsByCategoryData;
export const getAdminDashboardStatus = (state) => state.AdminDashboard.status;

export default AdminDashboardSlice.reducer;
